import {
  CopyButton,
  Loader,
  NumberInput,
  Select,
  Text,
  TextInput,
  UnstyledButton
} from "@marketplace/ui";
import { DatePickerInput } from "@marketplace/ui/dates";
import { useEffect, useMemo, useState } from "react";
import { Controller, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdCheck, MdContentCopy, MdDateRange } from "react-icons/md";

import { useGenerateRandomCodeQuery } from "@/hooks";
import { type GetProductListResponseDto } from "@/modules/product";

import type { CreateVoucherFormSchema } from "../../hooks/schemas/use-create-voucher-form-schema";

type CreateVoucherFormProps = {
  formMethods: UseFormReturn<CreateVoucherFormSchema>;
  productListData?: GetProductListResponseDto;
  productListLoading: boolean;
  productSearchQuery: string;
  setProductSearchQuery: (value: string) => void;
};

const getProductListData = (products: GetProductListResponseDto["data"]) => {
  return products.map((product) => ({
    label: product.name,
    value: product.id.toString()
  }));
};

const getProductPackagesData = (
  product: GetProductListResponseDto["data"][0]
) => {
  return product.product_package.map((pkg) => ({
    label: pkg.name,
    value: pkg.id.toString()
  }));
};

const getProductVariantsData = (
  variants: GetProductListResponseDto["data"][0]["product_package"][0]["variants"]
) => {
  return variants.map((variant) => ({
    label: variant.name,
    value: variant.id.toString()
  }));
};

export const CreateVoucherForm: React.FC<CreateVoucherFormProps> = ({
  formMethods,
  productListData,
  productListLoading,
  productSearchQuery,
  setProductSearchQuery
}) => {
  const { t } = useTranslation("voucher", {
    keyPrefix: "modal.createVoucherModal.form"
  });

  const {
    control,
    register,
    formState: { errors },
    resetField,
    watch,
    setValue
  } = formMethods;

  const data = productListData;

  const productId = watch("productId");
  const productPackageId = watch("productPackageId");
  const productVariantId = watch("productVariantId");
  const priceDiscount = watch("priceDiscount");
  const percentageDiscount = watch("percentageDiscount");
  const voucherCode = watch("voucherCode");

  const [lastEditedField, setLastEditedField] = useState<
    "price" | "percentage" | null
  >(null);

  const {
    data: randomCode,
    refetch: refetchRandomCode,
    isFetching: isGeneratingCode
  } = useGenerateRandomCodeQuery({
    queryParams: { length: 6, prefix: "VCH" }
  });

  useEffect(() => {
    const code = randomCode?.data.random_code;

    if (code) {
      setValue("voucherCode", code);
    }
  }, [randomCode, setValue]);

  const productsSelectData = useMemo(
    () => getProductListData(data?.data ?? []),
    [data]
  );

  const productPackagesSelectData = useMemo(() => {
    if (!data) return [];
    if (!formMethods.getValues("productId")) return [];

    const selectedProduct = data?.data.find(
      (product) => product.id === productId
    );

    if (!selectedProduct) return [];

    return getProductPackagesData(selectedProduct);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, productId]);

  const productVariantsSelectData = useMemo(() => {
    if (!data) return [];
    if (!productId) return [];
    if (!productPackageId) return [];

    const selectedProduct = data.data.find(
      (product) => product.id === productId
    );

    if (!selectedProduct) return [];

    const selectedPackage = selectedProduct.product_package.find(
      (pkg) => pkg.id === +productPackageId
    );

    if (!selectedPackage) return [];
    if (!selectedPackage.variants || selectedPackage.variants.length === 0)
      return [];

    return getProductVariantsData(selectedPackage.variants);
  }, [data, productId, productPackageId]);

  const oldProductPrice = useMemo(() => {
    if (!data) return 0;
    if (!productId) return 0;
    if (!productPackageId) return 0;
    if (!productVariantId) return 0;

    const selectedProduct = data.data.find(
      (product) => product.id === productId
    );

    if (!selectedProduct) return 0;

    const selectedPackage = selectedProduct.product_package.find(
      (pkg) => pkg.id === +productPackageId
    );

    if (!selectedPackage) return 0;
    if (!selectedPackage.variants || selectedPackage.variants.length === 0)
      return 0;

    const selectedVariant = selectedPackage.variants.find(
      (variant) => variant.id === +productVariantId
    );

    if (!selectedVariant) return 0;

    return selectedVariant.price_with_comission || selectedVariant.price;
  }, [data, productId, productPackageId, productVariantId]);

  useEffect(() => {
    if (productId) {
      resetField("productPackageId");
      resetField("productVariantId");
    }
  }, [productId, resetField]);

  useEffect(() => {
    if (productPackageId) {
      resetField("productVariantId");
    }
  }, [productPackageId, resetField]);

  useEffect(() => {
    resetField("percentageDiscount");
    resetField("priceDiscount");
    setLastEditedField(null);
  }, [productVariantId, productPackageId, productId, resetField]);

  const newPrice = useMemo(() => {
    if (!productVariantId) return 0;

    return oldProductPrice - priceDiscount;
  }, [productVariantId, oldProductPrice, priceDiscount]);

  useEffect(() => {
    if (lastEditedField !== "price" || !oldProductPrice || priceDiscount <= 0)
      return;

    // Calculate percentage for display only (not sent to backend)
    // Backend will recalculate using price WITH commission
    // Frontend uses price WITHOUT commission, so this is just for user preview
    const percentageDiscount = (priceDiscount / oldProductPrice) * 100;
    const newPercentage = Math.round(percentageDiscount * 100) / 100;

    if (newPercentage !== formMethods.getValues("percentageDiscount")) {
      setValue("percentageDiscount", newPercentage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceDiscount, oldProductPrice, lastEditedField]);

  useEffect(() => {
    const percentage = formMethods.getValues("percentageDiscount");
    if (lastEditedField !== "percentage" || !oldProductPrice || percentage <= 0)
      return;

    const newPriceDiscount = Math.round((percentage / 100) * oldProductPrice);
    if (newPriceDiscount !== priceDiscount) {
      setValue("priceDiscount", newPriceDiscount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [percentageDiscount, oldProductPrice, lastEditedField]);

  const copyVoucherButton = (copy: () => void, copied: boolean) => {
    if (copied) {
      return <MdCheck size={24} />;
    }

    return (
      <MdContentCopy size={24} onClick={copy} className="cursor-pointer" />
    );
  };

  return (
    <div className="space-y-[26px] p-[42px] pt-0">
      <TextInput
        {...register("voucherCode")}
        size="lg"
        placeholder={t("placeholders.voucherCode")}
        classNames={{
          label: "w-full"
        }}
        readOnly
        label={
          <div className="flex justify-between items-center">
            {t("labels.voucherCode")}

            <UnstyledButton
              className="text-noble-blue-200"
              onClick={() => refetchRandomCode()}
              disabled={isGeneratingCode}
            >
              <div className="flex items-center gap-2">
                {isGeneratingCode && <Loader size={16} />}
                <Text>{t("labels.generateRandomCode")}</Text>
              </div>
            </UnstyledButton>
          </div>
        }
        rightSection={
          <CopyButton value={voucherCode}>
            {({ copied, copy }) => copyVoucherButton(copy, copied)}
          </CopyButton>
        }
        rightSectionPointerEvents="all"
        error={errors.voucherCode?.message}
      />
      <TextInput
        {...register("customerEmail")}
        size="lg"
        placeholder={t("placeholders.customerEmail")}
        label={t("labels.customerEmail")}
        error={errors.customerEmail?.message}
      />
      <Controller
        control={control}
        name="productId"
        render={({ field }) => (
          <Select
            {...field}
            searchable
            searchValue={productSearchQuery}
            onSearchChange={setProductSearchQuery}
            data={productsSelectData}
            size="lg"
            label={t("labels.productName")}
            placeholder={t("placeholders.productName")}
            error={errors.productId?.message}
            onChange={(value) => field.onChange(value)}
            rightSection={productListLoading && <Loader size="sm" />}
          />
        )}
      />
      <Controller
        control={control}
        name="productPackageId"
        render={({ field }) => (
          <Select
            key={productPackageId}
            {...field}
            data={productPackagesSelectData}
            size="lg"
            label={t("labels.packageName")}
            placeholder={t("placeholders.productPackage")}
            error={errors.productPackageId?.message}
            onChange={(value) => field.onChange(value)}
            rightSection={productListLoading && <Loader size="sm" />}
            disabled={!productId || productListLoading}
            searchable
          />
        )}
      />
      <Controller
        control={control}
        name="productVariantId"
        render={({ field }) => (
          <Select
            key={productVariantId}
            {...field}
            data={productVariantsSelectData}
            size="lg"
            label={t("labels.productVariant")}
            placeholder={t("placeholders.productVariant")}
            error={errors.productVariantId?.message}
            onChange={(value) => field.onChange(value)}
            rightSection={productListLoading && <Loader size="sm" />}
            disabled={!productPackageId || productListLoading}
            searchable
          />
        )}
      />

      <NumberInput
        readOnly
        size="lg"
        label={t("labels.oldPrice")}
        placeholder={t("placeholders.oldPrice")}
        value={oldProductPrice}
        hideControls
      />

      <div className="grid grid-cols-2 gap-x-[26px]">
        <Controller
          control={control}
          name="percentageDiscount"
          render={({ field }) => (
            <NumberInput
              {...field}
              disabled={!productVariantId}
              size="lg"
              label={t("labels.discountPercent")}
              placeholder={t("placeholders.discountPercent")}
              description={t("descriptions.maxDiscountPercent")}
              value={field.value}
              onChange={(value) => {
                setLastEditedField("percentage");
                field.onChange(value);
              }}
              error={errors.percentageDiscount?.message}
              rightSection="%"
              min={1}
              max={99}
              clampBehavior="strict"
              hideControls
            />
          )}
        />
        <Controller
          control={control}
          name="priceDiscount"
          render={({ field }) => {
            const maxDiscount = Math.floor(oldProductPrice * 0.99);
            return (
              <NumberInput
                {...field}
                disabled={!productVariantId}
                size="lg"
                label={t("labels.discountAmount")}
                placeholder={t("placeholders.discountAmount")}
                description="&nbsp;"
                value={field.value}
                onChange={(value) => {
                  setLastEditedField("price");
                  field.onChange(value);
                }}
                error={errors.priceDiscount?.message}
                leftSection="IDR"
                min={1}
                max={maxDiscount}
                clampBehavior="strict"
                hideControls
              />
            );
          }}
        />
      </div>

      <NumberInput
        readOnly
        size="lg"
        label={t("labels.newPrice")}
        placeholder={t("placeholders.newPrice")}
        description={t("descriptions.newPriceIncludeTransactionFee")}
        value={newPrice}
        hideControls
      />

      <div className="grid grid-cols-2 gap-x-[26px]">
        <Controller
          control={control}
          name="validStartDate"
          render={({ field }) => (
            <DatePickerInput
              {...field}
              size="lg"
              label={t("labels.validityPeriod")}
              placeholder={t("placeholders.validityPeriod")}
              value={field.value}
              onChange={(value) => field.onChange(new Date(value as string))}
              error={errors.validStartDate?.message}
              rightSection={<MdDateRange size={24} />}
            />
          )}
        />
        <Controller
          control={control}
          name="validEndDate"
          render={({ field }) => (
            <DatePickerInput
              {...field}
              size="lg"
              label={t("labels.validityPeriod")}
              placeholder={t("placeholders.validityPeriod")}
              value={field.value}
              onChange={(value) => field.onChange(new Date(value as string))}
              error={errors.validEndDate?.message}
              rightSection={<MdDateRange size={24} />}
              classNames={{
                label: "opacity-0"
              }}
            />
          )}
        />
      </div>
    </div>
  );
};
